import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useApi } from "@/shared/utils/request";
import { entryQueryKeys } from "../entryQueryKeys";
import type { ApiClient } from "../types";
import {
	buildEntriesQuery,
	type EntriesQueryScope,
	entriesRequestTimekeeperIds,
	entriesScopeKey,
} from "./entries";

export type EntryCalendarSummaryDay = {
	date: string;
	total: number;
	entryCount: number;
	byStatus: Record<string, number>;
	statusCounts: Record<string, number>;
	byBillableCategory: Record<string, number>;
	billableCategoryCounts: Record<string, number>;
	byStatusBillableCategory: Record<string, Record<string, number>>;
	statusBillableCategoryCounts: Record<string, Record<string, number>>;
	byStatusBillable: Record<string, Record<string, number>>;
	statusBillableCounts: Record<string, Record<string, number>>;
};

type CalendarSummaryResponse = {
	object: "entry_calendar_summary";
	data: EntryCalendarSummaryDay[];
};

const STATUS_KEYS = ["tracked", "reviewed", "exported", "deleted"] as const;
const BILLABLE_CATEGORY_KEYS = [
	"billable",
	"non_billable",
	"billable_credit",
	"unknown",
] as const;
const BILLABLE_FLAG_KEYS = ["billable", "nonBillable"] as const;

function numberMap(
	value: unknown,
	keys: readonly string[],
): Record<string, number> {
	const source =
		value && typeof value === "object"
			? (value as Record<string, unknown>)
			: {};
	return Object.fromEntries(
		keys.map((key) => {
			const raw = source[key];
			return [key, typeof raw === "number" && Number.isFinite(raw) ? raw : 0];
		}),
	);
}

function nestedNumberMap(
	value: unknown,
	outerKeys: readonly string[],
	innerKeys: readonly string[],
): Record<string, Record<string, number>> {
	const source =
		value && typeof value === "object"
			? (value as Record<string, unknown>)
			: {};
	return Object.fromEntries(
		outerKeys.map((key) => [key, numberMap(source[key], innerKeys)]),
	);
}

// "Credit" is retired in the UI: server buckets still report billable_credit
// separately, so fold it into non_billable at the parse boundary to match how
// entry rows are folded in the entries slice.
function foldCreditIntoNonBillable(
	map: Record<string, number>,
): Record<string, number> {
	const { billable_credit = 0, ...rest } = map;
	rest.non_billable = (rest.non_billable ?? 0) + billable_credit;
	return rest;
}

function foldCreditNested(
	map: Record<string, Record<string, number>>,
): Record<string, Record<string, number>> {
	return Object.fromEntries(
		Object.entries(map).map(([key, inner]) => [
			key,
			foldCreditIntoNonBillable(inner),
		]),
	);
}

function normalizeCalendarSummaryDay(raw: unknown): EntryCalendarSummaryDay {
	const day =
		raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
	const byStatus = numberMap(day.byStatus, STATUS_KEYS);
	const statusCounts = numberMap(day.statusCounts, STATUS_KEYS);
	const byBillableCategory = foldCreditIntoNonBillable(
		numberMap(day.byBillableCategory, BILLABLE_CATEGORY_KEYS),
	);
	const billableCategoryCounts = foldCreditIntoNonBillable(
		numberMap(day.billableCategoryCounts, BILLABLE_CATEGORY_KEYS),
	);
	const byStatusBillableCategory = foldCreditNested(
		nestedNumberMap(
			day.byStatusBillableCategory,
			STATUS_KEYS,
			BILLABLE_CATEGORY_KEYS,
		),
	);
	const statusBillableCategoryCounts = foldCreditNested(
		nestedNumberMap(
			day.statusBillableCategoryCounts,
			STATUS_KEYS,
			BILLABLE_CATEGORY_KEYS,
		),
	);
	return {
		date: typeof day.date === "string" ? day.date : "",
		total:
			typeof day.total === "number" && Number.isFinite(day.total)
				? day.total
				: Object.values(byStatus).reduce((sum, value) => sum + value, 0),
		entryCount:
			typeof day.entryCount === "number" && Number.isFinite(day.entryCount)
				? day.entryCount
				: Object.values(statusCounts).reduce((sum, value) => sum + value, 0),
		byStatus,
		statusCounts,
		byBillableCategory,
		billableCategoryCounts,
		byStatusBillableCategory,
		statusBillableCategoryCounts,
		byStatusBillable: nestedNumberMap(
			day.byStatusBillable,
			STATUS_KEYS,
			BILLABLE_FLAG_KEYS,
		),
		statusBillableCounts: nestedNumberMap(
			day.statusBillableCounts,
			STATUS_KEYS,
			BILLABLE_FLAG_KEYS,
		),
	};
}

export async function fetchEntryCalendarSummary(
	api: ApiClient,
	scope: EntriesQueryScope,
): Promise<EntryCalendarSummaryDay[]> {
	if (!scope.date?.from || !scope.date.to) return [];
	const { query, matchesNothing } = buildEntriesQuery(scope, {
		includeDate: false,
	});
	if (matchesNothing) return [];
	const timekeeperIds = entriesRequestTimekeeperIds(scope);

	// POST /query until the backend OpenAPI tooling supports the QUERY method.
	const res = await api.makeApiRequest({
		endpoint: "/v2/entries/calendar-summary/query",
		method: "POST",
		body: {
			admin: scope.adminMode === true,
			from: scope.date.from,
			to: scope.date.to,
			...(timekeeperIds.length > 0 ? { timekeeperIds } : {}),
			...(Object.keys(query).length > 0 ? { q: query } : {}),
		},
	});
	if (res.status === "ERROR") {
		throw new Error(res.error || "Failed to fetch calendar summary");
	}
	const body = res.data as CalendarSummaryResponse | undefined;
	return Array.isArray(body?.data)
		? body.data.map(normalizeCalendarSummaryDay).filter((day) => day.date)
		: [];
}

export function useEntryCalendarSummary(
	scope: EntriesQueryScope,
	options: { enabled?: boolean } = {},
) {
	const { user } = useAuth();
	const api = useApi();
	const userId = user?.id;
	const enabled =
		Boolean(userId) &&
		options.enabled !== false &&
		Boolean(scope.date?.from && scope.date.to);

	const query = useQuery({
		queryKey: entryQueryKeys.calendarSummary(userId, entriesScopeKey(scope)),
		queryFn: () => fetchEntryCalendarSummary(api, scope),
		enabled,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	return {
		data: query.data ?? [],
		isLoading: query.isLoading && query.data === undefined,
		isFetching: query.isFetching,
		isSuccess: query.isSuccess,
		error: query.error instanceof Error ? query.error.message : null,
	};
}
